import type {
  Ability,
  CharacterStats,
  MovementStats,
} from '../../CharacterTypes';

// Priest — H2000's dedicated healer archetype (holy/shadow duality).
// Cloth caster with the highest mana pool and magic defense of any class;
// trades raw damage output for healing, shielding, and shadow DoT utility.
// Stat translation from the legacy priest_classic reference profile (high intellect/spirit, low
// strength, cloth armor) into H2000's stat shape — judgment translation,
// not a mechanical field copy. Numbers cross-checked against MageConfig
// (closest cloth caster) and PaladinConfig (closest support hybrid).
export const PRIEST_CONFIG = {
  name: 'Lightbearer',
  description: 'Cloth-clad healer wielding holy light and shadow — mends wounds, shields allies, and corrodes foes with mind magic',
  baseStats: {
    strength: 6,
    dexterity: 7,
    intelligence: 15,
    luck: 14,
    attack: 8,
    magicAttack: 22,
    defense: 5,
    magicDefense: 18,
    criticalRate: 0.05
  } as CharacterStats,
  healthMultiplier: 0.75,
  manaMultiplier: 1.5,
  color: '#F5F5F5',
  movementStats: {
    jumpPower: 1100,
    targetJumpHeight: 200,
    moveSpeed: 290,
    gravityScale: 0.9,
    terminalVelocity: 1000,
    acceleration: 3000,
    deceleration: 6000
  } as MovementStats,
  abilities: [
    {
      id: 'priest_auto',
      name: 'Sacred Touch',
      damage: 8,
      manaCost: 0,
      cooldown: 600,
      lastUsed: 0,
      range: 450,
      castTime: 0,
      description: 'Basic holy bolt — quick ranged auto attack.',
      icon: '/icons/abilities/priest_auto.svg'
    },
    {
      id: 'flash_mend',
      name: 'Flash Mend',
      damage: 0,
      manaCost: 15,
      cooldown: 4000,
      lastUsed: 0,
      range: 400,
      castTime: 0,
      description: 'Fast, cheap instant heal for a single ally.',
      icon: '/icons/abilities/flash_mend.svg'
    },
    {
      id: 'greater_mend',
      name: 'Greater Mend',
      damage: 0,
      manaCost: 30,
      cooldown: 8000,
      lastUsed: 0,
      range: 400,
      castTime: 800,
      description: 'Slow, powerful heal that restores heavy wounds.',
      icon: '/icons/abilities/greater_mend.svg'
    },
    {
      id: 'word_of_shielding',
      name: 'Word of Shielding',
      damage: 0,
      manaCost: 25,
      cooldown: 10000,
      lastUsed: 0,
      range: 400,
      castTime: 0,
      description: 'Shield an ally, absorbing incoming damage.',
      icon: '/icons/abilities/word_of_shielding.svg'
    },
    {
      id: 'shadow_dart',
      name: 'Shadow Dart',
      damage: 18,
      manaCost: 12,
      cooldown: 3000,
      lastUsed: 0,
      range: 450,
      castTime: 0,
      description: 'Hurl a dart of shadow magic at an enemy.',
      icon: '/icons/abilities/shadow_dart.svg'
    },
    {
      id: 'divine_hymn',
      name: 'Divine Hymn',
      damage: 0,
      manaCost: 50,
      cooldown: 20000,
      lastUsed: 0,
      range: 200,
      castTime: 1000,
      description: 'Channel a holy hymn that heals all nearby allies.',
      icon: '/icons/abilities/divine_hymn.svg'
    },
    {
      id: 'fade',
      name: 'Fade',
      damage: 0,
      manaCost: 10,
      cooldown: 20000,
      lastUsed: 0,
      range: 0,
      castTime: 0,
      description: 'Phase into the spirit realm, dropping threat and repositioning.',
      icon: '/icons/abilities/fade.svg'
    }
  ] as Ability[]
};
